/* eslint-disable @typescript-eslint/no-empty-interface, jsx-a11y/no-static-element-interactions,
jsx-a11y/click-events-have-key-events */
import React, { ReactNode, useEffect, useState } from 'react';
import { ReactConfirmProps } from 'react-confirm';
import { ThemeProvider } from '@emotion/react';
import {
  Button,
  CssBaseline,
  Dialog as MUIDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  StyledEngineProvider,
  Breakpoint,
} from '@mui/material';
import { twMerge } from 'tailwind-merge';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'src';
import {
  CloseIcon, DangerIcon, SuccessIcon, WarningIcon,
} from '../../icons';
import theme from '../../theme';
import Loader from '../Loader';

export interface DialogContentProps {
  close?: () => void;
  proceed?: () => void;
  showDialogLoading?: (message: string) => void;
  hideDialogLoading?: () => void;
}

export interface OptionsProps {
  /** Variant of the dialog */
  variant: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'info' | '';
  /** Flag to hide the primary button */
  hidePrimaryBtn?: boolean;
  /** Flag to hide the secondary button */
  hideSecondaryBtn?: boolean;
  /** Content of the dialog */
  dialogContent?: ReactNode;
  /** Label for the primary button */
  primaryBtnLabel?: string;
  /** Label for the secondary button */
  secondaryBtnLabel?: string;
  /** Flag to indicate loading state */
  isLoading?: boolean;
  /** Maximum width of the dialog */
  maxWidth?: Breakpoint;
  /** Flag to indicate full-width dialog */
  fullWidth?: boolean;
  /** Additional CSS classes for the dialog container */
  containerClasses?: string;
  /** Flag to show a close button */
  showCloseButton?: boolean;

  /** on modal close callback */
  onClose?: () => void;

  /** disable backdrop click close */
  disableBackdropClickClose?: boolean;

  /** disable escape button click close */
  disableEscapeKeyDownClose?: boolean;

  /** Flag to automatically close popup */
  autoClose?: boolean;
}

interface DialogProps extends ReactConfirmProps {
  /** Confirmation message */
  confirmation: string;
  /** Flag to indicate if the dialog is shown */
  show: boolean;
  /** Callback function when proceeding with the dialog */
  proceed: (response?: string | boolean) => void;
  /** Options for the dialog */
  options?: OptionsProps;
}

const Dialog: React.FC<DialogProps> = function Dialog(props) {
  const {
    confirmation,
    show,
    proceed,
    options,
  } = props;

  const {
    variant,
    autoClose = false,
    hidePrimaryBtn = false,
    hideSecondaryBtn = false,
    primaryBtnLabel = 'OK',
    secondaryBtnLabel = 'Cancel',
    dialogContent = undefined,
    isLoading = false,
    maxWidth = 'md',
    containerClasses,
    fullWidth = false,
    showCloseButton = false,
    disableBackdropClickClose = false,
    disableEscapeKeyDownClose = false,
    onClose,
  } = options || {};
  const [isOpen, setIsOpen] = useState(show);
  const [loadingState, setLoading] = useState<{ loading: boolean, message: null | string }>({
    loading: false, message: null,
  });

  /**
   * instead of controlling using show, now controlling using isOpen state.
   * As soon as the show value changes, it will first update the isOpen using below useEffect
   * In turn dialog is being controlled using isOpen
   */
  useEffect(() => {
    setIsOpen(show);
  }, [show]);

  /**
   * as soon as the isOpen or autoClose changes this useEffect will be called.
   * Check if autoCLone as a prop exists or not.
   * if autoclose exists then make isOpen = false after 3 seconds.
   * after that clear the timeout.
   */
  useEffect(() => {
    let timeoutId: any;
    if (autoClose && isOpen) {
      timeoutId = setTimeout(() => {
        setIsOpen(false);
        proceed();
      }, 3000);
    }
    return () => {
      clearTimeout(timeoutId);
    };
  }, [autoClose, isOpen]);

  const getIcon = () => {
    if (!options) {
      return null;
    }
    switch (variant) {
      case 'danger':
        return <DangerIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'success':
        return <SuccessIcon />;
      default: return undefined;
    }
  };

  const hideDialogLoading = () => {
    setLoading({
      loading: false,
      message: null,
    });
  };

  const showDialogLoading = (message = 'Please Wait...') => {
    setLoading({
      loading: true,
      message,
    });
  };

  let DialogCustomContent = dialogContent;
  if (dialogContent && React.isValidElement(dialogContent)) {
    DialogCustomContent = React.cloneElement(dialogContent as React.ReactElement, {
      close: () => proceed(),
      proceed: () => proceed(true),
      showDialogLoading,
      hideDialogLoading,
    });
  }

  const handleClose = (event: any, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason && reason === 'backdropClick' && disableBackdropClickClose) {
      return;
    }
    if (reason && reason === 'escapeKeyDown' && disableEscapeKeyDownClose) {
      return;
    }
    proceed(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <MUIDialog
            classes={{ paper: 'shadow-none' }}
            open={isOpen}
            fullWidth={fullWidth}
            // open={Boolean(show)}
            onClose={handleClose}
            aria-describedby="alert-dialog-slide-description"
            transitionDuration={{
              enter: 500,
              exit: 500,
            }}
            maxWidth={maxWidth}
          >
            {loadingState.loading && (
              <div className="min-h-[50vh] absolute top-0 left-0 right-0 bottom-0 z-10 flex items-center justify-center">
                <div className="absolute top-0 left-0 right-0 bottom-0 bg-[#fff] opacity-60" />
                <div className="z-10">
                  <Loader text={loadingState.message as string} />
                </div>
              </div>
            )}
            {
              showCloseButton && (
                <div className="absolute text-right mr-2 right-2 top-2 z-50" onClick={() => proceed()}>
                  <CloseIcon className="cursor-pointer" />
                </div>
              )
            }
            <div className={twMerge('p-10', containerClasses)}>
              {getIcon()}
              {confirmation && <DialogTitle sx={{ textAlign: 'center', padding: '0px' }}>{confirmation}</DialogTitle>}
              {DialogCustomContent && (
                <DialogContent className="p-0 overflow-auto">
                  {DialogCustomContent}
                </DialogContent>
              )}
              {(!hideSecondaryBtn || !hidePrimaryBtn) && (
                <DialogActions
                  sx={{
                    marginTop: 1,
                    marginBottom: 2,
                    justifyContent: 'center',
                  }}
                >
                  {!hideSecondaryBtn
                    && (
                      <Button
                        color="primary"
                        variant="outlined"
                        size="large"
                        disabled={isLoading}
                        onClick={() => proceed(false)}
                      >
                        {secondaryBtnLabel}
                      </Button>
                    )}
                  {!hidePrimaryBtn
                    && (
                      <Button
                        color="primary"
                        variant="contained"
                        size="large"
                        disabled={isLoading}
                        onClick={() => proceed(true)}
                      >
                        {primaryBtnLabel}
                      </Button>
                    )}
                </DialogActions>
              )}
            </div>
          </MUIDialog>
        </ThemeProvider>
      </StyledEngineProvider>
    </QueryClientProvider>
  );
};

Dialog.defaultProps = {
  options: {
    variant: 'primary',
    maxWidth: 'md',
    fullWidth: false,
    containerClasses: '',
    showCloseButton: false,
    disableBackdropClickClose: false,
    disableEscapeKeyDownClose: false,
    onClose: () => { },
  },
};

Dialog.propTypes = {

};

export default Dialog;
